/**
 * Shared currency formatting — consolidates the two independent
 * `Intl.NumberFormat('en-IN', { style: 'currency', ... })` implementations
 * that existed in `Analytics.tsx` and `RecentActivityList.tsx` before this
 * feature. Amounts are always the smallest currency unit (paise for INR),
 * never a float — see Product Catalog architecture §3/§6.
 */

/**
 * `exact: true` shows fractional currency units (up to 2 decimal places,
 * only as many as the amount actually needs — a whole-rupee amount still
 * renders with no trailing ".00"). Use this for any figure that is a
 * binding payable amount (a checkout total, a per-line price/discount/tax
 * breakdown row) — the default, rounded-to-whole-rupee display is only
 * appropriate for non-binding, presentational prices (a catalog card's "from
 * ₹499"), where losing paise-level precision is a legitimate simplification,
 * not a customer-facing accuracy problem the way it would be on a payment
 * screen (caught in code review of the Pricing Engine checkout UI).
 */
export function formatPrice(
  amountInPaise: number,
  currency: string = 'INR',
  options: { exact?: boolean } = {},
): string {
  const major = amountInPaise / 100
  // In exact mode, only show decimals when the amount actually has a
  // fractional rupee — `minimumFractionDigits: 0, maximumFractionDigits: 2`
  // alone lets Intl drop a single trailing zero (e.g. 100050 paise →
  // "₹1,000.5" instead of "₹1,000.50"), which is wrong for a binding payable
  // amount (caught in code review of the Pricing Engine checkout UI).
  const hasFraction = options.exact && amountInPaise % 100 !== 0
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(major)
  } catch {
    return `${currency} ${major.toLocaleString()}`
  }
}
